import { Cross } from '@upright/ui/icons';
import type { ReactNode } from 'react';
import { Button, Dialog, Modal, ModalOverlay } from 'react-aria-components';
import styles from './unified-shell-drawer.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The global search, which the top bar hands over at this width. Above the rail, outside its scroll. */
  search?: ReactNode;
  children: ReactNode;
}

/**
 * The navigation drawer the unified shell's rail becomes below `md` (PLT-6738): the same rail,
 * off-canvas, over a scrim, opened from the top bar's menu button.
 *
 * **Why a drawer and not the stacked pane.** `design/10-foundations.md` "Layout and responsive"
 * stacks the corporate shell's pane above its content at this width and forbids a drawer. That rule
 * is the corporate shell's, and it is narrowed there rather than broken here: this rail opens
 * expanded, carries a conversation list that grows without bound, and sits beside a chat surface
 * that pins its composer to the foot of the viewport. Stacked, it filled the whole phone and put
 * every page below the fold — see `views/internal/proto/unified-shell-mobile.tsx`, which holds both
 * patterns side by side.
 *
 * **It is a container, not a second rail.** The caller passes `UnifiedInvestorNavigation`, and the
 * frame renders that component *either* here or in the pane, never both, so there is exactly one
 * rail mounted and its chat-history subscription runs once.
 *
 * **Built on `react-aria-components`, like the design system's `Modal`.** Focus trap, focus
 * restore, Escape and dismiss-on-scrim come from `ModalOverlay`/`Dialog` rather than from four
 * hand-rolled listeners — the failure mode of rolling them is a background that stays reachable by
 * Tab while a modal surface is up, which is invisible to a mouse review. It is not the design
 * system's `Modal`: that one is a centred box with a title and a close chin, and a nav drawer has
 * neither. A second drawer in the product is what promotes this into
 * `common/generic/components/` (DESIGN.md "The rule of two"); one does not.
 *
 * `data-color-mode` / `data-light-theme` are repeated here for the reason `Modal.tsx` documents:
 * the overlay is portalled to `document.body`, escaping the `ThemeProvider` wrapper that declares
 * Primer's functional colour variables, so without them everything inside paints unstyled and
 * silently.
 */
export function UnifiedShellDrawer({ isOpen, onClose, search, children }: Props) {
  return (
    <ModalOverlay
      className={styles.overlay}
      data-color-mode="light"
      data-light-theme="light"
      isDismissable
      isOpen={isOpen}
      onOpenChange={newIsOpen => {
        if (!newIsOpen) onClose();
      }}
    >
      <Modal className={styles.panel}>
        <Dialog aria-label="Navigation" className={styles.dialog}>
          <div className={styles.chin}>
            <Button className={styles.close} aria-label="Close navigation" slot="close">
              <Cross width={20} height={20} />
            </Button>
          </div>
          {/*
           * The global search, which the bar gives up at this width: a burger, the search field at
           * its 160px floor and three utility controls do not fit a 390px bar, and the floor is a
           * rule rather than something to shave (design/40-shell-and-pages.md "Top bar"). It sits
           * above the rail rather than inside its scroll, so it stays put while the rows move.
           */}
          {search !== undefined && <div className={styles.search}>{search}</div>}
          <div className={styles.rail}>{children}</div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
